// §11.4 / D-016 — the Browser: real chrome, real frames, and an honest answer when a site says no.
//
// **The whole design turns on there being no content proxy** (D-015). Hangar never fetches a
// third-party page, so there is exactly one way to find out whether a site permits framing: put it
// in a frame and see. A site that refuses produces no `load` event in the frame, so the detector is
// a timer — and when it fires, the app says so in PLAN's words and offers the one thing that does
// work, a real browser tab.
//
// That is why §11.4 calls this app *honest*: it cannot show you every site, and the alternative to
// admitting it is an open proxy on a public URL.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { HangarAPI } from '@hangar/shared';
import { COPY } from '@/copy';
import { BROWSER_FRAME_TIMEOUT_MS } from '@/config';
import {
  HOME_URL,
  addHistory,
  canGoBack,
  canGoForward,
  faviconFor,
  internalPageOf,
  isBookmarked,
  navigate,
  newTab,
  resolveAddress,
  step,
  titleFor,
  toggleBookmark,
  type Bookmark,
  type BrowserTab,
  type HistoryEntry,
} from './model';

interface Persisted {
  readonly history: readonly HistoryEntry[];
  readonly bookmarks: readonly Bookmark[];
}

export function Browser({ api }: { api: HangarAPI }) {
  const [tabs, setTabs] = useState<readonly BrowserTab[]>(() => [newTab()]);
  const [activeId, setActiveId] = useState(() => tabs[0]?.id ?? '');
  const [address, setAddress] = useState('');
  const [history, setHistory] = useState<readonly HistoryEntry[]>([]);
  const [bookmarks, setBookmarks] = useState<readonly Bookmark[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const active = tabs.find((tab) => tab.id === activeId) ?? tabs[0];

  // §11.4 — history and bookmarks survive a close, through the app's own session blob (§4.1).
  useEffect(() => {
    void api.session.load().then((blob) => {
      const saved = blob as Persisted | null;
      if (!saved) return;
      setHistory(saved.history ?? []);
      setBookmarks(saved.bookmarks ?? []);
    });
  }, [api]);

  useEffect(() => {
    void api.session.save({ history, bookmarks } satisfies Persisted);
  }, [api, history, bookmarks]);

  useEffect(() => setAddress(active?.url ?? ''), [active?.id, active?.url]);

  const update = useCallback((id: string, next: (tab: BrowserTab) => BrowserTab) => {
    setTabs((current) => current.map((tab) => (tab.id === id ? next(tab) : tab)));
  }, []);

  /**
   * The refusal detector's backstop: a frame that never loads at all — a dead host, a hung
   * connection — is reported as a refusal too, because the user's options are the same either way.
   *
   * It is *only* the backstop. The primary signal is in `onLoad` below, because a blocked frame
   * fires `load` as readily as a working one.
   */
  const arm = useCallback(
    (id: string) => {
      const existing = timers.current.get(id);
      if (existing) clearTimeout(existing);
      timers.current.set(
        id,
        setTimeout(() => {
          timers.current.delete(id);
          update(id, (tab) => (tab.status === 'loading' ? { ...tab, status: 'refused' } : tab));
        }, BROWSER_FRAME_TIMEOUT_MS),
      );
    },
    [update],
  );

  useEffect(
    () => () => {
      for (const timer of timers.current.values()) clearTimeout(timer);
      timers.current.clear();
    },
    [],
  );

  /**
   * §11.4 — ask the server whether this URL permits framing.
   *
   * **The client cannot answer this itself.** A cross-origin frame blocked by `frame-ancestors`
   * fires `load` exactly as a working one does and throws the same `SecurityError` when inspected —
   * measured, not assumed (D-168). So §11.4 puts the check on the server, where the *headers* are
   * readable, and says guests go without it: `null` here means "no server, or no session", which is
   * §11.4's optimistic path and leaves the frame to try.
   */
  const probeFraming = useCallback(async (url: string): Promise<boolean | null> => {
    try {
      const response = await fetch(`/api/framing?url=${encodeURIComponent(url)}`, {
        credentials: 'same-origin',
      });
      if (!response.ok) return null;
      const body = (await response.json()) as { framable?: unknown };
      return typeof body.framable === 'boolean' ? body.framable : null;
    } catch {
      return null;
    }
  }, []);

  const go = useCallback(
    (raw: string) => {
      if (!active) return;
      const url = resolveAddress(raw);
      update(active.id, (tab) => navigate(tab, url));
      setHistory((current) => addHistory(current, { url, title: titleFor(url), at: Date.now() }));
      if (internalPageOf(url)) return;

      arm(active.id);
      void probeFraming(url).then((framable) => {
        if (framable === false) {
          const timer = timers.current.get(active.id);
          if (timer) clearTimeout(timer);
          timers.current.delete(active.id);
          // Only if the tab is still on that URL — a fast second navigation must not be
          // interstitialled by the first one's answer.
          update(active.id, (tab) => (tab.url === url ? { ...tab, status: 'refused' } : tab));
        }
      });
    },
    [active, arm, probeFraming, update],
  );

  /**
   * §10.2 — a URL handed to this window: a Share, or an app that launched us with one (D-540).
   *
   * §34.3's markdown preview is the first caller: an `http(s)` link in a preview opens here, which
   * is the only honest place for it — the preview is a pane inside an editor, and a link is a
   * navigation. `resolveAddress` still decides what the string means, so a payload carrying
   * `javascript:` becomes a search exactly as a typed one does (§19.6).
   */
  useEffect(() => {
    api.transfer.onReceive((payload) => {
      const url = payload.items.find((item) => item.mime === 'text/uri-list')?.text?.trim();
      if (url !== undefined && url.length > 0) go(url);
    });
  }, [api, go]);

  const walk = useCallback(
    (delta: number) => {
      if (!active) return;
      update(active.id, (tab) => step(tab, delta));
      const target = active.history[active.index + delta];
      if (target && !internalPageOf(target)) arm(active.id);
    },
    [active, arm, update],
  );

  const openOutside = useCallback((url: string) => {
    // §11.4's Open-in-a-new-tab. `noopener` because the page must not get a handle on the shell.
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  if (!active) return null;

  const page = internalPageOf(active.url);
  const favicon = faviconFor(active.url);
  // §19.1 makes this impossible anyway; refusing it here means the sandbox above never depends on
  // a response header arriving to stay safe.
  const refused = active.status === 'refused' || framesOwnOrigin(active.url);

  return (
    <div className="browser" data-testid="browser">
      <div className="browser-tabs" role="tablist" data-testid="browser-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === activeId}
            className={`browser-tab${tab.id === activeId ? ' is-active' : ''}`}
            data-testid={`browser-tab-${tab.id}`}
            onClick={() => setActiveId(tab.id)}
          >
            <span className="browser-tab-title">{tab.title}</span>
            {tabs.length > 1 && (
              <span
                className="browser-tab-close"
                data-testid={`browser-tab-close-${tab.id}`}
                aria-label={COPY.browser.closeTab}
                onClick={(event) => {
                  event.stopPropagation();
                  setTabs((current) => {
                    const remaining = current.filter((candidate) => candidate.id !== tab.id);
                    if (remaining.length === 0) return current;
                    if (tab.id === activeId) setActiveId(remaining.at(-1)?.id ?? '');
                    return remaining;
                  });
                }}
              >
                ✕
              </span>
            )}
          </button>
        ))}
        <button
          type="button"
          className="browser-tab-new"
          data-testid="browser-new-tab"
          aria-label={COPY.browser.newTab}
          onClick={() => {
            const tab = newTab();
            setTabs((current) => [...current, tab]);
            setActiveId(tab.id);
          }}
        >
          +
        </button>
      </div>

      <div className="browser-toolbar">
        <button
          type="button"
          data-testid="browser-back"
          aria-label={COPY.browser.back}
          disabled={!canGoBack(active)}
          onClick={() => walk(-1)}
        >
          ‹
        </button>
        <button
          type="button"
          data-testid="browser-forward"
          aria-label={COPY.browser.forward}
          disabled={!canGoForward(active)}
          onClick={() => walk(1)}
        >
          ›
        </button>
        <button
          type="button"
          data-testid="browser-reload"
          aria-label={COPY.browser.reload}
          onClick={() => go(active.url)}
        >
          ⟳
        </button>

        <div className="browser-address">
          {favicon && (
            <img
              className="browser-favicon"
              data-testid="browser-favicon"
              src={favicon}
              alt=""
              // A site without a favicon is not an error; the slot simply empties.
              onError={(event) => (event.currentTarget.style.visibility = 'hidden')}
            />
          )}
          <input
            type="text"
            data-testid="browser-address"
            aria-label={COPY.browser.addressLabel}
            placeholder={COPY.browser.addressPlaceholder}
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              go(address);
            }}
          />
        </div>

        <button
          type="button"
          data-testid="browser-bookmark"
          aria-label={isBookmarked(bookmarks, active.url) ? COPY.browser.bookmarked : COPY.browser.bookmark}
          data-on={String(isBookmarked(bookmarks, active.url))}
          onClick={() =>
            setBookmarks((current) =>
              toggleBookmark(current, { url: active.url, title: active.title }),
            )
          }
        >
          {isBookmarked(bookmarks, active.url) ? '★' : '☆'}
        </button>
      </div>

      <div className="browser-viewport" data-testid="browser-viewport">
        {page ? (
          <InternalPage
            page={page}
            history={history}
            bookmarks={bookmarks}
            onOpen={(url) => go(url)}
          />
        ) : refused ? (
          <Interstitial url={active.url} onOpen={() => openOutside(active.url)} />
        ) : (
          <iframe
            key={`${active.id}:${active.url}`}
            className="browser-frame"
            data-testid="browser-frame"
            title={active.title}
            src={active.url}
            /*
             * §19.2 — "Origin is the boundary; sandbox is defense in depth."
             *
             * `allow-same-origin` is here on purpose and is not the footgun it looks like: it lets
             * the framed page keep **its own** origin instead of an opaque one, which for a
             * third-party URL is still not ours. The dangerous combination is
             * `allow-scripts allow-same-origin` on content that is *same-origin with the embedder*
             * — that content could reach out and remove its own sandbox. Two things stop that here:
             * §19.1 serves Hangar with `frame-ancestors 'none'`, so the shell cannot be framed even
             * by its own Browser, and `framesOwnOrigin` below refuses it explicitly rather than
             * trusting a header to hold (D-168).
             *
             * Without it, the frame is opaque-origin and the refusal detector cannot tell a blocked
             * page from a loaded one — both throw.
             */
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
            referrerPolicy="no-referrer"
            onLoad={() => {
              // A blocked frame fires this too (D-168), so `load` means only "the browser is done
              // trying" — it clears the hang timer and nothing more. Whether the page is actually
              // there is §11.4's probe, above.
              const timer = timers.current.get(active.id);
              if (timer) clearTimeout(timer);
              timers.current.delete(active.id);
              update(active.id, (tab) =>
                tab.status === 'refused' ? tab : { ...tab, status: 'loaded' },
              );
            }}
          />
        )}
      </div>
    </div>
  );
}

/** Would this URL frame the shell itself? §19.1 forbids it; this refuses to rely on that alone. */
function framesOwnOrigin(url: string): boolean {
  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return false;
  }
}

/** §11.4 — the PLAN-bound refusal, and the one action that actually works. */
function Interstitial({ url, onOpen }: { url: string; onOpen: () => void }) {
  const host = useMemo(() => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  }, [url]);

  return (
    <div className="browser-interstitial" data-testid="browser-interstitial">
      <h2 data-testid="browser-interstitial-title">{COPY.framingRefused}</h2>
      <p>{COPY.browser.framingBody(host)}</p>
      <button type="button" data-testid="browser-open-external" onClick={onOpen}>
        {COPY.framingOpenInNewTab}
      </button>
    </div>
  );
}

function InternalPage({
  page,
  history,
  bookmarks,
  onOpen,
}: {
  page: string;
  history: readonly HistoryEntry[];
  bookmarks: readonly Bookmark[];
  onOpen: (url: string) => void;
}) {
  return (
    <div className="browser-internal" data-testid={`browser-page-${page}`}>
      {page === 'home' && (
        <>
          <h2>{COPY.browser.homeHeading}</h2>
          <ul className="browser-list">
            {bookmarks.slice(0, 8).map((mark) => (
              <li key={mark.url}>
                <button type="button" onClick={() => onOpen(mark.url)}>
                  {mark.title}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {page === 'history' && (
        <>
          <h2>{COPY.browser.historyHeading}</h2>
          {history.length === 0 ? (
            <p data-testid="browser-history-empty">{COPY.browser.historyEmpty}</p>
          ) : (
            <ul className="browser-list">
              {history.map((entry) => (
                <li key={entry.url}>
                  <button
                    type="button"
                    data-testid={`browser-history-${entry.url}`}
                    onClick={() => onOpen(entry.url)}
                  >
                    {entry.title}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {page === 'bookmarks' && (
        <>
          <h2>{COPY.browser.bookmarksHeading}</h2>
          {bookmarks.length === 0 ? (
            <p data-testid="browser-bookmarks-empty">{COPY.browser.bookmarksEmpty}</p>
          ) : (
            <ul className="browser-list">
              {bookmarks.map((mark) => (
                <li key={mark.url}>
                  <button
                    type="button"
                    data-testid={`browser-bookmark-${mark.url}`}
                    onClick={() => onOpen(mark.url)}
                  >
                    {mark.title}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {page === 'about' && (
        <>
          <h2>{COPY.browser.aboutHeading}</h2>
          <p data-testid="browser-about-body">{COPY.browser.aboutBody}</p>
        </>
      )}
    </div>
  );
}

export { HOME_URL };
